/**
 * Hook for managing Pixlee widget instances with lazy loading support
 * Registry pattern prevents duplicate widget initialization
 * Tracks widgets by ID to prevent duplicates
 *
 *
 * @see https://developers.pixlee.com/docs/pixlee-widget-user-guide#lazy-loading
 */
import {useCallback, useEffect, useRef, useState} from 'react';

import {usePixleeScript} from './use-pixlee-script';

/**
 * Global widget registry - Tracks initialized widgets by containerId
 * Prevents duplicate widget initialization
 * Key: containerId, Value: { widgetId, initialized }
 */
const widgetRegistry = new Map();

function matchesPixleeWidgetIframe(iframe, widgetId) {
  const src = iframe.getAttribute('src') || '';

  if (!/pixlee|emplifi/i.test(src)) {
    return false;
  }

  const wid = String(widgetId);

  if (iframe.getAttribute('data-widget-id') === wid) {
    return true;
  }

  return src.includes(`widget_id=${wid}`) || src.includes(`widget_id=${encodeURIComponent(wid)}`);
}

function findPixleeIframe(container, widgetId) {
  if (!container) {
    return null;
  }

  for (const el of container.querySelectorAll('iframe')) {
    if (matchesPixleeWidgetIframe(el, widgetId)) {
      return el;
    }
  }

  return null;
}

function iframeHasSize(iframe) {
  const {height} = iframe.getBoundingClientRect();

  return height > 0 || iframe.offsetHeight > 0;
}

/**
 * Initialize a Pixlee widget by ID with custom container
 * Prevents duplicates - safe to call multiple times
 *
 * @param {string|number} widgetId - The Pixlee widget ID
 * @param {string} containerId - The container element ID for this widget
 * @returns {boolean} success - True if widget initialized successfully
 */
function initializeWidget(widgetId, containerId) {
  if (!widgetId || !window.Pixlee || !containerId) {
    return false;
  }

  const registryKey = containerId;

  // Already initialized for this container - skip
  if (widgetRegistry.has(registryKey)) {
    const existing = widgetRegistry.get(registryKey);

    if (existing.initialized && existing.widgetId === String(widgetId)) {
      return true;
    }
  }

  // Check if container element exists
  const containerElement = document.getElementById(containerId);

  if (!containerElement) {
    console.warn(`Pixlee: Container element #${containerId} not found`);

    return false;
  }

  try {
    window.Pixlee.addSimpleWidget({
      widgetId: Number(widgetId),
      containerId: containerId,
      subscribedEvents: ['widgetLoaded', 'photoOpened', 'ctaClicked'],
      fixedWidth: false,
    });

    // Track initialized widget
    widgetRegistry.set(registryKey, {
      widgetId: String(widgetId),
      initialized: true,
    });

    // Resize widget after initialization (as recommended by Pixlee docs)
    setTimeout(() => {
      if (window.Pixlee?.resizeWidget) {
        window.Pixlee.resizeWidget();
      }
    }, 100);

    return true;
  } catch (error) {
    console.error(`Pixlee widget initialization error for widget ${widgetId} in container ${containerId}:`, error);

    return false;
  }
}

/**
 * Cleanup widget by container ID
 * Removes from registry to allow re-initialization
 *
 * @param {string} containerId - The container element ID
 */
function cleanupWidget(containerId) {
  if (!containerId) {
    return;
  }

  widgetRegistry.delete(containerId);

  // Only close Pixlee if no widgets are active
  // Pass true to indicate this is a single page app
  if (widgetRegistry.size === 0 && window.Pixlee?.close) {
    try {
      window.Pixlee.close(true);
    } catch (error) {
      console.error('Pixlee cleanup error:', error);
    }
  }
}

/**
 * Initialize and manage a Pixlee widget with lazy loading support
 * Widget only initializes when container becomes visible in viewport
 *
 * @param {string|number} widgetId - The Pixlee widget ID from Contentful
 * @param {Object} options - Configuration options
 * @param {string} options.containerId - Custom container ID (required for multiple widgets)
 * @param {boolean} [options.lazyLoad=true] - Enable lazy loading (default: true)
 * @param {string} [options.rootMargin='200px'] - Intersection observer root margin for preloading
 * @returns {Object} { isReady, isVisible, containerRef, containerId }
 */
export function usePixleeWidget(widgetId, options = {}) {
  const {
    containerId: customContainerId,
    lazyLoad = true,
    rootMargin = '200px', // Preload when within 200px of viewport
  } = options;

  const isScriptReady = usePixleeScript();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const initializedRef = useRef(false);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const [observedElement, setObservedElement] = useState(null);
  const [isVisible, setIsVisible] = useState(!lazyLoad);

  // Callback ref ensures IntersectionObserver re-attaches when the target mounts
  const setContainerRef = useCallback((node) => {
    containerRef.current = node;
    setObservedElement(node);
  }, []);

  // Generate unique container ID if not provided
  const containerId = customContainerId || `pixlee_container_${widgetId}`;

  useEffect(() => {
    if (!lazyLoad) {
      setIsVisible(true);

      return;
    }

    if (!observedElement) {
      return;
    }

    if (
      !('IntersectionObserver' in window) ||
      !('IntersectionObserverEntry' in window) ||
      !('intersectionRatio' in window.IntersectionObserverEntry.prototype)
    ) {
      setIsVisible(true);

      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      rootMargin,
      threshold: 0,
    });

    observer.observe(observedElement);

    return () => {
      observer.disconnect();
    };
  }, [lazyLoad, observedElement, rootMargin]);

  // Determine if we should initialize
  // If lazy loading is disabled, initialize immediately when script is ready
  // If lazy loading is enabled, wait until element is visible
  const shouldInitialize = lazyLoad ? (isScriptReady && isVisible) : isScriptReady;

  useEffect(() => {
    // Wait for conditions to be met
    if (!shouldInitialize || !widgetId) {
      return;
    }

    const id = String(widgetId);

    // Same widget already initialized in this container - no action needed
    if (widgetIdRef.current === id && initializedRef.current) {
      return;
    }

    // Initialize widget with custom container ID
    let observer = null;
    let poll = null;
    let onMessage = null;

    if (initializeWidget(widgetId, containerId)) {
      widgetIdRef.current = id;
      initializedRef.current = true;

      const trySetReady = () => {
        const el = document.getElementById(containerId);

        if (!el) {
          return false;
        }

        const iframe = findPixleeIframe(el, widgetId);

        if (!iframe || !iframeHasSize(iframe)) {
          return false;
        }

        setIsWidgetReady(true);
        if (observer) observer.disconnect();
        if (poll) clearInterval(poll);
        if (onMessage) window.removeEventListener('message', onMessage);
        return true;
      };

      onMessage = (event) => {
        if (!event.source) {
          return;
        }

        const el = document.getElementById(containerId);

        if (!el) {
          return;
        }

        for (const iframe of el.querySelectorAll('iframe')) {
          if (iframe.contentWindow !== event.source) {
            continue;
          }

          if (matchesPixleeWidgetIframe(iframe, widgetId)) {
            trySetReady();
          }

          return;
        }
      };
      window.addEventListener('message', onMessage);

      if (!trySetReady()) {
        const root = document.getElementById(containerId);

        if (root) {
          observer = new MutationObserver(trySetReady);
          observer.observe(root, {childList: true, subtree: true});
        }

        let attempts = 0;
        poll = setInterval(() => {
          if (trySetReady() || ++attempts >= 10) {
            if (observer) observer.disconnect();
            if (poll) clearInterval(poll);
          }
        }, 250);
      }
    }

    return () => {
      if (observer) observer.disconnect();
      if (poll) clearInterval(poll);
      if (onMessage) window.removeEventListener('message', onMessage);
      if (widgetIdRef.current === id) {
        cleanupWidget(containerId);
        widgetIdRef.current = null;
        initializedRef.current = false;
        setIsWidgetReady(false);
      }
    };
  }, [shouldInitialize, widgetId, containerId]);

  return {
    isReady: isWidgetReady,
    isVisible,
    containerRef: setContainerRef,
    containerId,
  };
}
